import { CheckCheck } from "lucide-react";
import { formatMessageTime, shouldShowDateDivider, getDateDivider } from "@/shared/utils/dates";
import { cn } from "@/shared/utils/cn";
import { useState, useEffect } from "react";
import { storageService } from "@/lib/storage";

export type MessageDirection = "incoming" | "outgoing";

export interface Message {
  id: number;
  taskId: number | null;
  createdAt: string;
  direction: MessageDirection;
  senderName?: string | null;
  body: string;
  photoUrl?: string | null; // URL of the photo if message contains a photo
}

interface MessageBubbleProps {
  message: Message;
  previousMessage?: Message;
  isOptimistic?: boolean;
}

export function MessageBubble({ message, previousMessage, isOptimistic = false }: MessageBubbleProps) {
  const showDateDivider = shouldShowDateDivider(
    message.createdAt,
    previousMessage?.createdAt
  );

  // Swap the logic: messages sent by the user (direction: "incoming" from API perspective) 
  // should appear on the right with blue background
  // Messages received from server (direction: "outgoing" from API perspective) should appear on the left with grey background
  const isUserMessage = message.direction === "incoming";
  const senderName = message.senderName || (isUserMessage ? "You" : "Unknown");

  // Debug logging
  if (message.photoUrl) {
  }

  // State for authenticated image URL
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  // Check if URL is a presigned URL (has query parameters) - these can be used directly
  const isPresignedUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.search.length > 0; // Has query parameters
    } catch {
      return false;
    }
  };

  // Load image - use presigned URLs directly, fetch others with auth
  useEffect(() => {
    if (message.photoUrl && !imageUrl && !imageError) {
      const loadImage = async () => {
        try {
          // If it's a presigned URL (has query params), use it directly
          if (isPresignedUrl(message.photoUrl!)) {
            setImageUrl(message.photoUrl!);
            return;
          }

          // Otherwise, fetch with authentication
          const token = await storageService.getAuthToken();
          if (!token) {
            console.error("No auth token available for image");
            setImageError(true);
            return;
          }

          // Fetch the image with auth headers
          const response = await fetch(message.photoUrl!, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            mode: 'cors',
          });

          if (!response.ok) {
            console.error("Failed to fetch image:", response.status, response.statusText);
            setImageError(true);
            return;
          }

          // Convert to blob and create object URL
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setImageUrl(objectUrl);
        } catch (error) {
          console.error("Error loading image:", error);
          setImageError(true);
        }
      };

      loadImage();
    }

    // Cleanup object URL on unmount (only if it's a blob URL)
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [message.photoUrl, imageUrl, imageError]);

  return (
    <div data-testid={`message-${message.id}`}>
      {showDateDivider && (
        <div className="flex items-center justify-center my-4" data-testid="date-divider">
          <span className="px-3 py-1 text-xs text-muted-foreground bg-muted rounded-full">
            {getDateDivider(message.createdAt)}
          </span>
        </div>
      )}

      <div
        className={cn(
          "flex items-start space-x-2 mb-4 w-full min-w-0",
          isUserMessage ? "justify-end" : "justify-start",
          isOptimistic && "opacity-70"
        )}
      >
        {!isUserMessage && (
          <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center text-white font-medium text-sm flex-shrink-0" data-testid="message-avatar">
            SD
          </div>
        )}

        <div className={cn("max-w-[85%] lg:max-w-[55%]", isUserMessage && "flex flex-col items-end")}>
          <div
            className={cn(
              "rounded-lg px-3 py-2 min-w-0",
              isUserMessage
                ? "bg-teal-500 text-white"
                : "bg-muted text-foreground"
            )}
            data-testid="message-bubble"
          >
            {/* Photo display */}
            {message.photoUrl && (
              <div className="mb-2">
                {imageError ? (
                  <div className="flex items-center justify-center p-4 bg-muted rounded-md text-sm text-muted-foreground">
                    Failed to load image
                  </div>
                ) : imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="Message photo"
                    className="max-w-full h-auto rounded-md"
                    style={{ maxHeight: "300px" }}
                    crossOrigin="anonymous"
                    onLoad={() => {}}
                    onError={(e) => {
                      console.error("Failed to load photo:", message.photoUrl, e);
                      setImageError(true);
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center p-4 bg-muted rounded-md">
                    <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            )}
            {/* Text content - only show if body is not empty */}
            {message.body && (
              <p className="text-sm whitespace-pre-wrap message-text">{message.body}</p>
            )}
          </div>

          <div
            className={cn(
              "flex items-center space-x-2 mt-1 text-xs text-muted-foreground",
              isUserMessage && "justify-end"
            )}
            data-testid="message-metadata"
          >
            <span>{formatMessageTime(message.createdAt)}</span>
            {isUserMessage && (
              <div data-testid="message-status">
                {isOptimistic ? (
                  <div className="h-3 w-3 rounded-full border-2 border-muted-foreground animate-pulse" />
                ) : (
                  <CheckCheck className="h-3 w-3 text-blue-500" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
